import type { InitCommandCtx } from '@blocksuite/std';
import type { BlockModel, Store } from '@blocksuite/store';

import { UserProvider } from '../../services/user-service';

export type BlockDeletionOutcome = 'deleted' | 'cancelled' | 'rejected';
export type BlockDeletionResult =
  | BlockDeletionOutcome
  | Promise<BlockDeletionOutcome>;
export type BlockDeletionOptions = {
  /** The blocks move elsewhere (cut): they leave no entry in the trash. */
  moving?: boolean;
  /** User who deletes; the gates fill it from the editor's user service. */
  deletedBy?: string;
  /** Canvas connectors that leave with the block; its trash entry keeps them. */
  connectors?: Record<string, unknown>[];
  /** The canvas group of the block and of each of those connectors, by id. */
  groups?: Record<string, string>;
};

/** Registered by StreamLain effects to avoid a circular package dependency. */
export let streamlainTrashcanHandler:
  | ((
      store: Store,
      model: BlockModel,
      options?: BlockDeletionOptions
    ) => BlockDeletionResult)
  | null = null;

export function registerStreamlainTrashcanHandler(
  handler: typeof streamlainTrashcanHandler
): void {
  streamlainTrashcanHandler = handler;
}

type DeleteModelsInput = {
  selectedModels?: BlockModel[];
  deleteOptions?: BlockDeletionOptions;
};
type DeleteModelsOutput = { deletionResults: Promise<BlockDeletionOutcome[]> };

/**
 * The page's entry point for user deletion of whole blocks. Store-only callers
 * (for example an image preview) use the same command without mounting an editor.
 */
export function deleteSelectedModelsCommand(
  ctx: DeleteModelsInput & { store: Store },
  next: (output: DeleteModelsOutput) => void
): void;
export function deleteSelectedModelsCommand(
  ctx: DeleteModelsInput & InitCommandCtx,
  next: (output?: DeleteModelsOutput) => void
): void;
export function deleteSelectedModelsCommand(
  ctx: DeleteModelsInput & ({ store: Store } | InitCommandCtx),
  next: (output: DeleteModelsOutput) => void
): void {
  const store = 'store' in ctx ? ctx.store : ctx.std.store;
  const deleteOptions: BlockDeletionOptions = {
    deletedBy:
      'std' in ctx
        ? ctx.std.getOptional(UserProvider)?.currentUserInfo$.peek()?.id
        : undefined,
    ...ctx.deleteOptions,
  };
  const models = ctx.selectedModels;
  if (!models) {
    console.error(
      '`selectedModels` is required, you need to use `getSelectedModels` command before adding this command to the pipeline.'
    );
    return;
  }

  const deletionResults = Promise.all(
    models.map(model => {
      if (store.readonly) return 'cancelled' as const;
      if (streamlainTrashcanHandler) {
        return streamlainTrashcanHandler(store, model, deleteOptions);
      }
      // The standalone BlockSuite editor has no StreamLain integration.
      store.deleteBlock(model);
      return 'deleted' as const;
    })
  );
  // Keyboard and toolbar chains remain synchronous. Callers that need to
  // refresh after a database confirmation can await the command's result.
  return next({ deletionResults });
}
